import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Input,
  VStack,
  useColorModeValue,
  useToast,
  Text,
  Link,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  InputGroup,
  InputRightElement,
  IconButton
} from '@chakra-ui/react'
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons'
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
// Importar servicios desde la configuración central (Convex)
import { authService } from '../config/index'

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [isResetting, setIsResetting] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const toast = useToast()
  const navigate = useNavigate()

  // Verificar si el usuario ya está autenticado al cargar la página
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const isValid = await authService.isTokenValid()
        
        if (isValid) {
          console.log("Usuario ya autenticado, redirigiendo a dashboard")
          // Si el usuario viene desde otra parte de la app y ya está autenticado,
          // redirigirlo automáticamente al dashboard
          setTimeout(() => {
            navigate('/direct-dashboard', { replace: true })
          }, 100)
        }
      } catch (error) {
        console.error("Error al verificar autenticación:", error)
      }
    }
    
    checkAuth()
  }, [navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      // Usar el servicio de autenticación (Convex)
      const result = await authService.login(email, password)
      
      if (!result) {
        throw new Error('Credenciales inválidas')
      }
      
      console.log("Usuario autenticado correctamente:", result.user.email)
      
      toast({
        title: 'Inicio de sesión exitoso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
      
      // Redirigir al dashboard normal (protegido)
      console.log("Login exitoso, redirigiendo a dashboard...");
      setTimeout(() => {
        // Usar setTimeout para dar tiempo a que se complete cualquier procesamiento pendiente
        navigate('/dashboard', { replace: true });
      }, 500);
    } catch (error) {
      console.error("Error de autenticación:", error)
      toast({
        title: 'Error al iniciar sesión',
        description: 'Credenciales inválidas',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!resetEmail.trim()) {
      toast({
        title: 'Error',
        description: 'Por favor, introduce tu dirección de correo electrónico',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    setIsResetting(true)

    try {
      // Solicitar token de recuperación
      const token = await authService.requestPasswordReset(resetEmail)
      
      if (!token) {
        toast({
          title: 'Usuario no encontrado',
          description: 'No se encontró ninguna cuenta asociada a este correo electrónico',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
        return
      }

      // Importar dinámicamente el servicio de email
      const { emailService } = await import('../config/index')
      
      // Enviar correo de recuperación
      await emailService.sendPasswordResetEmail(resetEmail, token)
      
      toast({
        title: 'Correo enviado',
        description: 'Se ha enviado un enlace de recuperación a tu correo electrónico. Revisa tu bandeja de entrada y la carpeta de spam.',
        status: 'success',
        duration: 8000,
        isClosable: true,
      })
      
      setResetEmail('')
      onClose()
    } catch (error) {
      console.error('Error al procesar recuperación de contraseña:', error)
      toast({
        title: 'Error',
        description: 'No se pudo procesar la solicitud de restablecimiento. Por favor, inténtalo de nuevo más tarde.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsResetting(false)
    }
  }

  return (
    <Box 
      minH="100vh" 
      backgroundImage="url('/silla.jpg')"
      backgroundSize="cover"
      backgroundPosition="center"
      position="relative"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="whiteAlpha.700"
      />
      <Container maxW="container.sm" py={{ base: 8, md: 20 }} position="relative" zIndex={1}>
        <VStack spacing={8} align="stretch">
          <Heading
            textAlign="center"
            fontSize={{ base: '3xl', md: '4xl' }}
            fontWeight="bold"
            bgGradient="linear(to-r, brand.600, brand.500, brand.400)"
            bgClip="text"
            letterSpacing="tight"
          >
            Acceso Técnicos
          </Heading>

          <Box
            as="form"
            onSubmit={handleSubmit}
            bg={useColorModeValue('whiteAlpha.900', 'blackAlpha.600')}
            p={{ base: 6, md: 8 }}
            borderRadius="2xl"
            boxShadow="2xl"
            backdropFilter="blur(20px)"
            border="1px solid"
            borderColor={useColorModeValue('gray.100', 'whiteAlpha.100')}
            transition="all 0.3s ease"
            _hover={{
              transform: 'translateY(-2px)',
              boxShadow: '2xl',
              borderColor: useColorModeValue('brand.100', 'brand.700'),
            }}
          >
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel fontWeight="medium">Email</FormLabel>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="correo@ejemplo.com"
                  size="lg"
                  bg={useColorModeValue('white', 'whiteAlpha.50')}
                  borderWidth="2px"
                  _hover={{
                    borderColor: 'brand.400',
                  }}
                  _focus={{
                    borderColor: 'brand.500',
                    boxShadow: 'outline',
                  }}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontWeight="medium">Contraseña</FormLabel>
                <InputGroup size="lg">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    bg={useColorModeValue('white', 'whiteAlpha.50')}
                    borderWidth="2px"
                    _hover={{
                      borderColor: 'brand.400',
                    }}
                    _focus={{
                      borderColor: 'brand.500',
                      boxShadow: 'outline',
                    }}
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                      icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                      variant="ghost"
                      onClick={() => setShowPassword(!showPassword)}
                      size="sm"
                      color="gray.500"
                      _hover={{
                        color: 'brand.500',
                      }}
                    />
                  </InputRightElement>
                </InputGroup>
              </FormControl>

              <Button
                type="submit"
                colorScheme="brand"
                size="lg"
                width="full"
                isLoading={isLoading}
                loadingText="Iniciando sesión..."
                fontSize="md"
                fontWeight="bold"
                py={7}
                _hover={{
                  transform: 'translateY(-1px)',
                  boxShadow: 'lg',
                }}
              >
                Iniciar Sesión
              </Button>
              
              <Text textAlign="center" mt={2}>
                <Link 
                  color="brand.500" 
                  fontWeight="medium" 
                  onClick={onOpen}
                  _hover={{ textDecoration: 'underline' }}
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </Text>
            </VStack>
          </Box>

          {/* Modal para recuperar contraseña */}
          <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Recuperar contraseña</ModalHeader>
              <ModalCloseButton />
              <ModalBody pb={6}>
                <Text mb={4}>
                  Introduce tu correo electrónico y te enviaremos un enlace para restablecer tu contraseña.
                </Text>
                <FormControl>
                  <FormLabel>Email</FormLabel>
                  <Input 
                    type="email" 
                    value={resetEmail} 
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="correo@ejemplo.com" 
                  />
                </FormControl>
              </ModalBody>

              <ModalFooter>
                <Button 
                  colorScheme="brand" 
                  mr={3} 
                  onClick={handleResetPassword}
                  isLoading={isResetting}
                  loadingText="Enviando..."
                >
                  Enviar correo
                </Button>
                <Button onClick={onClose}>Cancelar</Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </VStack>
      </Container>
    </Box>
  )
}
